import React, { useState, useEffect } from 'react';
import { SwiperSlide } from 'swiper/react';
import { CapaQuotationServiceType } from '@generated/graphql';
import { IFile } from '@routes/Common/type';
import { clsx } from '@components/Provider';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import Swiper from './Swiper';
import { ChevronLeft, ChevronRight, Image } from '@carbon/icons-react';
import { Enum_Action_Button_Enum } from '@hooks/useUserActionLog';
import useStyles from './useStyles';
import ThumbnailSlide from './ThumbnailSlide';
import useFiles from '@hooks/useFiles';
import axios from 'axios';
import { getImageUrl } from '@utils/urls';
import openViewer from '@utils/popupViewer';

interface ValidatedFile {
  file: IFile;
  isError: boolean;
  imageUrl?: string;
}

interface Props {
  matches?: boolean;
  rfqId?: string;
  serviceType?: CapaQuotationServiceType;
  files: Array<IFile>;
  canDownload: boolean;
}

const RfqFilesThumbnailViewerPresenter: React.FC<Props> = ({
  matches,
  rfqId,
  serviceType,
  files,
  canDownload
}) => {
  const classes = useStyles({
    matches: matches,
    isPagination: files.length > 1
  });
  const [selectedFileName, setSelectedFileName] = useState(
    files?.[0]?.fileName
  );
  const { onDownloadFile } = useFiles();

  const [validatedFiles, setValidatedFiles] = useState<ValidatedFile[] | null>(
    null
  );
  const [currentButtonStatus, setCurrentButtonStatus] = useState(true);

  useEffect(() => {
    const getFiles = async () => {
      const responseAll = await Promise.all(
        files.map(async (file) => {
          try {
            const response = await axios(getImageUrl(file.fileId, serviceType));
            return {
              file,
              isError: false,
              imageUrl: response.config?.url
            };
          } catch (e) {
            return {
              file,
              isError: true
            };
          }
        })
      );

      setValidatedFiles(responseAll);
      setCurrentButtonStatus(responseAll[0]?.isError);
    };
    getFiles();
  }, [files, serviceType]);

  useEffect(() => {
    if (files && files.length > 0) {
      setSelectedFileName(files[0].fileName);
    }
  }, [rfqId, files]);

  const FileNameDisplay = () => {
    return (
      <CustomizedTypography
        className={classes.fileName}
        variant="subtitle2"
        color="textSecondary"
        noWrap
      >
        {selectedFileName}
      </CustomizedTypography>
    );
  };

  const SwiperNextBtn = () => {
    return (
      <div className={clsx(classes.swiperNextBtn, classes.swiperBtn)}>
        <ChevronRight />
      </div>
    );
  };

  const SwiperPrevBtn = () => {
    return (
      <div className={clsx(classes.swiperPrevBtn, classes.swiperBtn)}>
        <ChevronLeft />
      </div>
    );
  };

  if (files.length < 1 || !validatedFiles) return null;

  return (
    <div className={classes.container}>
      <div className={classes.root}>
        <FileNameDisplay />
        {validatedFiles.length > 0 && (
          <div className={classes.swiperWrapper}>
            <Swiper
              initialSlide={0}
              PrevBtn={files.length === 1 ? undefined : SwiperPrevBtn}
              NextBtn={files.length === 1 ? undefined : SwiperNextBtn}
              slidesPerView={1}
              onSlideChange={(swiper) => {
                const index =
                  (files.length + swiper.activeIndex - 1) % files.length;
                setSelectedFileName(validatedFiles[index]?.file?.fileName);

                setCurrentButtonStatus(
                  validatedFiles.find(
                    ({ file }) => file?.fileId === files[index].fileId
                  )?.isError ?? false
                );
              }}
              pagination={{ type: 'fraction' }}
              loop={files.length === 1 ? false : true}
            >
              {validatedFiles.map(({ file, imageUrl }) => {
                return (
                  <SwiperSlide
                    key={file.fileId}
                    className={classes.swiperSlide}
                  >
                    <ThumbnailSlide
                      imageUrl={imageUrl}
                      handleDownloadFile={() => onDownloadFile(file)}
                      canDownload={canDownload}
                    />
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        )}
      </div>
      <div className={classes.viewerBtn}>
        <CustomizedButton
          onClick={() => {
            openViewer({
              chatRoomId: '',
              rfqId: rfqId,
              selectedFileId: validatedFiles?.find(
                (file) => file.file.fileName === selectedFileName
              )?.file?.fileId
            });
          }}
          logId={Enum_Action_Button_Enum.Button_Open_Viewer}
          color="default"
          size="large"
          startIcon={<Image size={20} />}
          disabled={currentButtonStatus}
        >
          뷰어로 보기
        </CustomizedButton>
      </div>
    </div>
  );
};

export default RfqFilesThumbnailViewerPresenter;
